<!-- 商户看板页面 -->

<template>
  <page-header-wrapper>
    <div class="merchant-dashboard-container">
      <!-- 概述部分 -->
      <merchant-overview :stats-data="mergedOverviewData" />

      <div class="dashboard-section">
        <a-space direction="vertical" :size="24" style="width: 100%">
          <a-row :gutter="24">
            <a-col :span="24">
              <!-- 订单数量分布 -->
              <order-count-chart
                :chart-data="chartData.orderCount"
                @handleOrderCountTypeChange="handleOrderCountTypeChange"
              />
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="24">
              <!-- 订单金额分布 -->
              <order-distribution-chart
                :chart-data="chartData.orderDistribution"
                @handleOrderTypeChange="handleOrderTypeChange"
              />
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="24">
              <!-- 订单成功率分布 -->
              <success-rate-chart
                :chart-data="chartData.successRate"
                @handleSucRateTypeChange="handleSucRateTypeChange"
              />
            </a-col>
          </a-row>
          <!-- 7日交易金额 -->
          <a-row :gutter="24">
            <a-col :span="24">
              <order-seven-trend
                :chart-data="chartData.sevenDaysTrend"
                @handleSevenTrendTypeChange="handleSevenTrendTypeChange"
              />
            </a-col>
          </a-row>
        </a-space>
      </div>
    </div>
  </page-header-wrapper>
</template>

<script>
import {
  getDashboardSum,
  getHourlyOrderCount,
  getSevenDaysTrend,
  getHourlyOrderAmountSum,
  getHourlyOrderSuccessRate
} from '@/api/dashboard'

import MerchantOverview from './components/MerchantOverview'
import OrderCountChart from './components/OrderCountChart'
import OrderDistributionChart from './components/OrderDistributionChart'
import SuccessRateChart from './components/SuccessRateChart'
import OrderSevenTrend from './components/OrderSevenTrend'
// import SimpleTestChart from './components/SimpleTestChart'

export default {
  name: 'MerchantDashboard',
  components: {
    MerchantOverview,
    OrderCountChart,
    OrderDistributionChart,
    SuccessRateChart,
    OrderSevenTrend
  },
  data () {
    return {
      // 概述数据
      overviewData: {},
      // 看板汇总数据
      dashboardSumData: null,
      // 账户统计数据
      accountData: {},
      // 订单统计数据
      orderSummaryData: {},
      // 图表数据
      chartData: {
        orderCount: {},
        orderDistribution: {},
        successRate: {},
        sevenDaysTrend: {}
      },
      // 加载状态
      loading: {
        overview: false,
        charts: false
      }
    }
  },
  mounted () {
    this.initDashboard()
  },
  methods: {
    // 初始化看板数据
    async initDashboard () {
      try {
        // 设置加载状态
        this.loading.overview = true
        this.loading.charts = true

        // 获取概述相关数据
        await Promise.all([
          this.getDashboardSumData()
        ])
        this.loading.overview = false

        // 获取图表数据 所有接口并行执行，相互不影响
        const chartPromises = [
          this.getHourlyOrderCountData(), // 订单数量分布
          this.getOrderDistributionData(), // 订单金额分布
          this.getSuccessRateData(), // 订单成功率分布
          this.getSevenDaysTrendData() // 七日交易金额趋势
        ]

        // 使用 allSettled 确保所有请求都完成，即使某个失败也不影响其他
        const results = await Promise.allSettled(chartPromises)

        // 统计成功和失败的请求
        let successCount = 0
        let failureCount = 0

        // 记录失败的请求，但不阻断页面加载
        results.forEach((result, index) => {
          if (result.status === 'rejected') {
            const methodNames = [
              'getHourlyOrderCountData',
              'getOrderDistributionData',
              'getSuccessRateData',
              'getSevenDaysTrendData'
            ]
            console.warn(`${methodNames[index]} 加载失败:`, result.reason)
            failureCount++
          } else {
            successCount++
          }
        })

        this.loading.charts = false

        // 显示加载结果提示（仅在开发环境显示详细信息）
        if (this.isDevelopment) {
          if (failureCount > 0 && successCount > 0) {
            this.$message.warning(`部分图表数据加载失败（${failureCount}/${results.length}）`)
          } else if (failureCount === results.length) {
            this.$message.error('所有图表数据加载失败')
          }
        }
      } catch (error) {
        this.loading.overview = false
        this.loading.charts = false
        console.error('获取看板数据失败', error)
        this.$message.error('获取看板数据失败')
      }
    },

    // 获取看板汇总数据
    async getDashboardSumData () {
      try {
        const response = await getDashboardSum()
        // console.log('总数量：：：', response)
        this.dashboardSumData = response || {}
        // console.log('dashboardSumData updated:', this.dashboardSumData)
        // 强制触发响应式更新
        this.$forceUpdate()
      } catch (error) {
        console.error('获取看板汇总数据失败', error)
        this.dashboardSumData = {}
      }
    },

    // 获取订单分布数据
    async getOrderDistributionData (type = '0') {
      try {
        const response = await getHourlyOrderAmountSum(type)
        this.chartData.orderDistribution = response || {}
      } catch (error) {
        // console.error('获取订单分布数据失败', error)
        this.chartData.orderDistribution = {}
      }
    },

    // 获取成功率数据
    async getSuccessRateData (type = '0') {
      try {
        const response = await getHourlyOrderSuccessRate(type)
        this.chartData.successRate = response || {}
      } catch (error) {
        console.error('获取成功率数据失败', error)
        this.chartData.successRate = {}
      }
    },

          // 订单类型变化处理
      handleOrderTypeChange (type) {
        this.getOrderDistributionData(type)
      },

      // 成功率类型变化处理
      handleSucRateTypeChange (type) {
        this.getSuccessRateData(type)
      },

    // 获取订单数量分布数据
    async getHourlyOrderCountData (type = '0') {
      try {
        const response = await getHourlyOrderCount(type)
        console.log('订单数量', response)
        this.chartData.orderCount = response || {}
      } catch (error) {
        console.error('获取订单数量分布数据失败', error)
        this.chartData.orderCount = {}
      }
    },

    // 订单数量类型变化处理
    handleOrderCountTypeChange (type) {
      this.getHourlyOrderCountData(type)
    },

    // 获取七日交易金额趋势数据
    async getSevenDaysTrendData (type = '0') {
      try {
        const response = await getSevenDaysTrend(type)
        // 确保response是对象类型，包含sevenDaysData属性
        if (response && typeof response === 'object') {
          this.chartData.sevenDaysTrend = response
        } else {
          // 如果返回的是数组，则包装成对象
          this.chartData.sevenDaysTrend = { sevenDaysData: response || [] }
        }
      } catch (error) {
        console.error('获取七日交易金额趋势数据失败', error)
        this.chartData.sevenDaysTrend = { sevenDaysData: [] }
      }
    },

    // 七日趋势类型变化处理
    handleSevenTrendTypeChange (type) {
      this.getSevenDaysTrendData(type)
    }
  },

  computed: {
    // 判断是否为开发环境
    isDevelopment () {
      return process.env.NODE_ENV === 'development'
    },
    // 合并概述数据
    mergedOverviewData () {
      if (!this.dashboardSumData) {
        return null
      }
      const merged = {
        ...this.dashboardSumData
      }
      // console.log('mergedOverviewData computed:', merged)
      return merged
    }
  }
}
</script>

<style lang="less" scoped>
@import './dashboard.less';
</style>
